import { useState } from 'react';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { qdfAPI } from '../../../services/api';
import { generatePDFBytes } from '../services/pdfService';

export const useBulkOperations = (entries, onSuccess) => {
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  // Bulk delete is confirmed by a themed dialog owned by the page; this only
  // tracks that a confirmation is pending.
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedCards(new Set());
  };

  const handleCardSelect = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleSelectAll = (paginatedEntries) => {
    if (selectedCards.size === paginatedEntries.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(paginatedEntries.map(entry => entry._id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedCards.size === 0) return;
    setIsConfirmingDelete(true);
  };

  const cancelBulkDelete = () => setIsConfirmingDelete(false);

  const confirmBulkDelete = async () => {
    const count = selectedCards.size;

    setIsBulkDeleting(true);
    try {
      await Promise.all(Array.from(selectedCards).map((id) => qdfAPI.deleteReport(id)));

      toast.success(`Deleted ${count} ${count === 1 ? 'report' : 'reports'}`);
      setSelectedCards(new Set());
      onSuccess();
    } catch (err) {
      console.error('Error deleting reports:', err);
      toast.error('Failed to delete some reports');
    } finally {
      setIsBulkDeleting(false);
      setIsConfirmingDelete(false);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedCards.size === 0 || isBulkDownloading) return;

    setIsBulkDownloading(true);

    try {
      const selectedEntries = Array.from(selectedCards)
        .map(cardId => entries.find(e => e._id === cardId))
        .filter(Boolean);

      if (selectedEntries.length === 0) {
        toast.warn('No valid entries found for download');
        return;
      }

      const zip = new JSZip();
      
      const pdfPromises = selectedEntries.map(async (entry) => {
        try {
          const maintenanceType = entry.maintenanceType || (entry.ppmType ? 'preventive' : 'corrective');
          const { pdfBytes, fileName } = await generatePDFBytes(entry, maintenanceType);
          zip.file(fileName, pdfBytes);
          return { success: true, fileName };
        } catch (error) {
          console.error('PDF generation error for', entry.scNumber, ':', error);
          return { success: false, scNumber: entry.scNumber, error };
        }
      });

      const results = await Promise.all(pdfPromises);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length === 0) {
        toast.error('Failed to generate any PDFs');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const currentDate = new Date().toISOString().split('T')[0];
      const zipFileName = `QDF_Reports_${currentDate}_${successful.length}files.zip`;
      
      saveAs(zipBlob, zipFileName);

      toast.success(`Downloaded ${successful.length} PDF${successful.length === 1 ? '' : 's'} as a ZIP`);
      if (failed.length > 0) {
        toast.warn(
          `${failed.length} file(s) failed to generate: ${failed.map((f) => f.scNumber).join(', ')}`
        );
      }

      setSelectedCards(new Set());
    } catch (error) {
      console.error('Bulk download error:', error);
      toast.error('Failed to create ZIP file. Please try again.');
    } finally {
      setIsBulkDownloading(false);
    }
  };

  return {
    selectedCards,
    isSelectionMode,
    isBulkDownloading,
    isConfirmingDelete,
    isBulkDeleting,
    toggleSelectionMode,
    handleCardSelect,
    handleSelectAll,
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,
    handleBulkDownload
  };
};
